import { useState, useEffect, useMemo, useCallback } from "react"
import { useAuth0 } from "@auth0/auth0-react"

// Utility function for debounce
const debounce = (func, wait) => {
  let timeout
  return (...args) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

export const useCheckApplicationStatus = (positionIds) => {
  const [applicationStatus, setApplicationStatus] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { getAccessTokenSilently } = useAuth0()
  const apiUrl = process.env.REACT_APP_API_URL

  // Memoize the positionIds array
  const memoizedPositionIds = useMemo(() => positionIds, [positionIds])

  // Memoize the fetch function
  const fetchApplicationStatus = useCallback(async () => {
    if (!memoizedPositionIds || memoizedPositionIds.length === 0) {
        console.log('memoizedIds: ' + memoizedPositionIds)
      setLoading(false)
      return
    }

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      const response = await fetch(`${apiUrl}/api/JobApplications/checkMultiple`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(memoizedPositionIds), // Send the array directly
      })
      if (response.ok) {
        const data = await response.json()
        setApplicationStatus((prevStatus) => ({ ...prevStatus, ...data }))
      } else {
        const errorData = await response.json()
        throw new Error(errorData.title || "Failed to check application status")
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [getAccessTokenSilently, apiUrl, memoizedPositionIds])

  // Debounce the fetch function
  const debouncedFetchApplicationStatus = useCallback(debounce(fetchApplicationStatus, 300), [fetchApplicationStatus])

  useEffect(() => {
    debouncedFetchApplicationStatus()
  }, [debouncedFetchApplicationStatus])

  return { applicationStatus, loading, error }
}

